import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConnectionManager = ({ connections, onConnect, onDisconnect }) => {
  const [connectingService, setConnectingService] = useState(null);

  const services = [
    {
      id: 'github',
      name: 'GitHub',
      description: 'Connect repositories for code analysis and documentation',
      icon: 'Github',
      color: 'from-gray-700 to-gray-900',
      glowColor: 'shadow-gray-500/20',
      features: ['Repository access', 'Code analysis', 'Issue tracking', 'Pull request data']
    },
    {
      id: 'make',
      name: 'Make.com',
      description: 'Automate workflows and integrate with 1000+ apps',
      icon: 'Zap',
      color: 'from-blue-500 to-blue-700',
      glowColor: 'shadow-blue-500/20',
      features: ['Workflow automation', 'API integrations', 'Data transformation', 'Trigger management']
    },
    {
      id: 'codex',
      name: 'Codex Cloud',
      description: 'Advanced AI processing and model deployment',
      icon: 'Cloud',
      color: 'from-purple-500 to-purple-700',
      glowColor: 'shadow-purple-500/20',
      features: ['AI model access', 'Cloud processing', 'Scalable compute', 'Real-time inference']
    }
  ];

  const handleConnect = async (serviceId) => {
    setConnectingService(serviceId);
    
    // Simulate OAuth flow
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    onConnect(serviceId);
    setConnectingService(null);
  };

  const handleDisconnect = (serviceId) => {
    onDisconnect(serviceId);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Service Connections
        </h2>
        <p className="text-text-secondary">
          Connect external services to enhance your agent's capabilities
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {services?.map((service) => {
          const isConnected = connections?.[service?.id];
          const isConnecting = connectingService === service?.id;
          
          return (
            <div
              key={service?.id}
              className={`bg-card border border-border rounded-xl p-6 transition-all duration-300 hover:shadow-lg ${
                isConnected ? `${service?.glowColor} shadow-lg` : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${service?.color} rounded-lg flex items-center justify-center ${
                  isConnected ? 'animate-pulse-soft' : ''
                }`}>
                  <Icon 
                    name={service?.icon} 
                    size={24} 
                    color="white" 
                    strokeWidth={2}
                  />
                </div>
                
                <div className={`w-3 h-3 rounded-full ${
                  isConnected ? 'bg-success animate-pulse-soft' : 'bg-muted'
                }`} />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                {service?.name}
              </h3>
              <p className="text-text-secondary text-sm mb-4">
                {service?.description}
              </p>
              <div className="mb-4">
                <h4 className="text-xs font-medium text-card-foreground mb-2 uppercase tracking-wide">
                  Features:
                </h4>
                <ul className="space-y-1">
                  {service?.features?.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 text-xs text-text-secondary">
                      <Icon name="Dot" size={12} className="text-muted-foreground" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-2">
                {isConnected ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-success">
                      <Icon name="CheckCircle" size={16} />
                      <span>Connected</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      onClick={() => handleDisconnect(service?.id)}
                      iconName="Unlink"
                      iconPosition="left"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    loading={isConnecting}
                    onClick={() => handleConnect(service?.id)}
                    iconName="Link"
                    iconPosition="left"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectionManager;